import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO, parse } from "date-fns";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAvailabilityColor = (estimated, installed, pending) => {
  const totalUsed = (installed || 0) + (pending || 0);
  const available = (estimated || 0) - totalUsed;
  const percentage = estimated > 0 ? (available / estimated) * 100 : 0;
  if (percentage >= 60)
    return {
      color: "bg-green-400",
      textColor: "text-green-400",
      percentage,
      available,
    };
  if (percentage >= 20)
    return {
      color: "bg-yellow-400",
      textColor: "text-yellow-400",
      percentage,
      available,
    };
  return {
    color: "bg-red-400",
    textColor: "text-red-400",
    percentage,
    available,
  };
};

const getProgressColor = (pct) => {
  if (pct >= 90) return { bar: "bg-green-400", text: "text-green-400" };
  if (pct >= 50) return { bar: "bg-blue-400", text: "text-blue-400" };
  return { bar: "bg-yellow-400", text: "text-yellow-400" };
};

// ---------------------------------------------------------------------------
// AlertDialog
// ---------------------------------------------------------------------------
const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-red-400 hover:bg-red-500",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-blue-400" />,
      iconBg: "bg-gray-600",
      buttonBg: "bg-blue-500 hover:bg-blue-600",
    },
  };
  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-10 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold text-blue-400"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-200">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm sm:w-auto transition-colors duration-200`}
              >
                {confirmText}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 sm:w-auto transition-colors duration-200"
              >
                {cancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// ProgressLogDetail
// ---------------------------------------------------------------------------
const ProgressLogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

  // ── Core state ──
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // ── Edit state ──
  const [editMode, setEditMode] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [editNotes, setEditNotes] = useState("");
  const [editWorkDate, setEditWorkDate] = useState("");

  // ── Add items state ──
  const [additionalItems, setAdditionalItems] = useState([]);
  const [addItemQuantities, setAddItemQuantities] = useState({});
  const [showAddItems, setShowAddItems] = useState(false);
  const [addItemsLoading, setAddItemsLoading] = useState(false);
  const [addItemBidAreaFilter, setAddItemBidAreaFilter] = useState("");
  const [addItemBidAreas, setAddItemBidAreas] = useState([]);

  // ── Filters / view ──
  const [bidAreaFilter, setBidAreaFilter] = useState("");
  const [bidAreas, setBidAreas] = useState([]);
  const [groupByTitle, setGroupByTitle] = useState(false);

  // ── Dialog ──
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // ── Rejection reason modal ──
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // ---------------------------------------------------------------------------
  // Fetch log + enrich with net-assemblies data
  // ---------------------------------------------------------------------------
  const fetchLog = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE}/progressLogs/${id}`, {
        headers,
      });
      const logData = response.data.data;

      // Enrich with net-assemblies so budget/remaining columns match WorkOrderView
      const projectId = logData.project?._id || logData.project;
      const workOrderId = logData.workOrder?._id || logData.workOrder;

      if (projectId && workOrderId) {
        try {
          const netRes = await axios.get(
            `${API_BASE}/net-assemblies/project/${projectId}`,
            { headers, params: { category: "L" } },
          );

          const allNetLabor = netRes.data?.data || [];
          const woNetItems = allNetLabor.filter((item) => {
            const itemWO = item.linkedWO?._id
              ? item.linkedWO._id.toString()
              : (item.linkedWO || "").toString();
            return itemWO === workOrderId.toString();
          });

          const netItemsMap = new Map();
          woNetItems.forEach((item) => {
            const keyId = (
              item.lineItemId ||
              item.key ||
              item._id ||
              ""
            ).toString();
            if (keyId) netItemsMap.set(keyId, item);
            const origId = (item.originalLineItemId || "").toString();
            if (origId && !netItemsMap.has(origId))
              netItemsMap.set(origId, item);
          });

          if (logData.lineItems && netItemsMap.size > 0) {
            logData.lineItems = logData.lineItems.map((item) => {
              const lineItemId = (item.lineItemId || "").toString();
              const netItem =
                netItemsMap.get(lineItemId) ||
                netItemsMap.get((item.originalLineItemId || "").toString());

              if (netItem) {
                return {
                  ...item,
                  currentEstimated: netItem.netRequiredQty || 0,
                  currentApproved: netItem.qtyOrdered || 0,
                  currentPending: netItem.qtyPending || 0,
                  currentAvailable: netItem.netRemaining || 0,
                  conditionName:
                    netItem.conditionName || item.conditionName || "",
                  sectionNumber:
                    netItem.sectionNumber || item.sectionNumber || "",
                  isFromVariance:
                    item.isFromVariance ||
                    netItem.hasVariance ||
                    netItem.isNewSpec ||
                    false,
                  closedOutBySizeChange: netItem.closedOutBySizeChange || false,
                  isNewSpec: netItem.isNewSpec || false,
                };
              }
              return item;
            });
          }
        } catch (netErr) {
          console.warn(
            "Failed to fetch net-assemblies, using backend data:",
            netErr.message,
          );
        }
      }

      setLog(logData);

      const initialQuantities = {};
      logData.lineItems?.forEach((item) => {
        initialQuantities[item.lineItemId] = item.qtyInstalled || 0;
      });
      setQuantities(initialQuantities);
      setEditNotes(logData.notes || "");
      setEditWorkDate(
        logData.workCompletedDate
          ? logData.workCompletedDate.split("T")[0]
          : "",
      );

      const uniqueBidAreas = [
        ...new Set(
          logData.lineItems?.map((item) => item.bidArea).filter(Boolean),
        ),
      ];
      setBidAreas(uniqueBidAreas);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load progress log.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  useEffect(() => {
    if (!loading) window.scrollTo(0, 0);
  }, [loading]);

  // ---------------------------------------------------------------------------
  // Enter edit mode — also pre-loads available WO items for adding new ones
  // ---------------------------------------------------------------------------
  const enterEditMode = async () => {
    setEditMode(true);
    setShowAddItems(false);
    setAdditionalItems([]);
    setAddItemQuantities({});

    const workOrderId = log?.workOrder?._id || log?.workOrder;
    if (!workOrderId) return;

    try {
      setAddItemsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE}/progressLogs/work-order/${workOrderId}/available-items`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const allAvailable = response.data.items || [];

      // Exclude items already on this log
      const existingIds = new Set(
        (log.lineItems || []).map((li) => (li.lineItemId || "").toString()),
      );
      const newItems = allAvailable.filter(
        (item) => !existingIds.has((item._id || "").toString()),
      );

      setAdditionalItems(newItems);

      const initQtys = {};
      newItems.forEach((item) => {
        initQtys[item._id] = "";
      });
      setAddItemQuantities(initQtys);

      const areas = [
        ...new Set(newItems.map((item) => item.bidArea).filter(Boolean)),
      ];
      setAddItemBidAreas(areas);
      setAddItemBidAreaFilter("");
    } catch (err) {
      console.warn("Failed to fetch available items:", err.message);
    } finally {
      setAddItemsLoading(false);
    }
  };

  const exitEditMode = () => {
    setEditMode(false);
    setShowAddItems(false);
    setAdditionalItems([]);
    setAddItemQuantities({});
    const reset = {};
    log?.lineItems?.forEach((item) => {
      reset[item.lineItemId] = item.qtyInstalled || 0;
    });
    setQuantities(reset);
    setEditNotes(log?.notes || "");
    setEditWorkDate(
      log?.workCompletedDate ? log.workCompletedDate.split("T")[0] : "",
    );
  };

  // ---------------------------------------------------------------------------
  // Quantity handlers
  // ---------------------------------------------------------------------------
  const handleQuantityChange = (lineItemId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = log?.lineItems?.find((li) => li.lineItemId === lineItemId);

    if (item) {
      const maxAvailable =
        (item.currentAvailable || 0) + (item.qtyInstalled || 0);
      if (numValue > maxAvailable) {
        setDialog({
          open: true,
          type: "error",
          title: "Invalid Quantity",
          message: `Quantity cannot exceed ${maxAvailable} ${item.uom}.`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
    }
    setQuantities((prev) => ({ ...prev, [lineItemId]: numValue }));
  };

  const handleAddItemQuantityChange = (itemId, value) => {
    const numValue = parseFloat(value) || 0;
    const item = additionalItems.find((li) => li._id === itemId);

    if (item && numValue > item.qtyRemaining) {
      setDialog({
        open: true,
        type: "error",
        title: "Invalid Quantity",
        message: `Quantity cannot exceed ${item.qtyRemaining} ${item.uom}.`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      setAddItemQuantities((prev) => ({
        ...prev,
        [itemId]: item.qtyRemaining.toString(),
      }));
      return;
    }
    setAddItemQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  // ---------------------------------------------------------------------------
  // Save changes (edit)
  // ---------------------------------------------------------------------------
  const handleSaveChanges = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");

      const existingLineItems = log.lineItems
        .map((item) => ({
          lineItemId: item.lineItemId,
          assemblyId: item.assemblyId,
          qtyInstalled: parseFloat(quantities[item.lineItemId] || 0),
          title: item.title,
          costCodeDescription: item.costCodeDescription || item.title,
          uom: item.uom,
          unitCost: item.unitCost,
          bidArea: item.bidArea || "",
          conditionName: item.conditionName || "",
          sectionNumber: item.sectionNumber || "",
        }))
        .filter((item) => item.qtyInstalled > 0);

      const newLineItems = additionalItems
        .filter((item) => parseFloat(addItemQuantities[item._id] || 0) > 0)
        .map((item) => ({
          lineItemId: item._id,
          assemblyId: item.assemblyId,
          qtyInstalled: parseFloat(addItemQuantities[item._id]),
          title: item.title,
          costCodeDescription: item.costCodeDescription || item.title,
          uom: item.uom,
          unitCost: item.unitCost,
          bidArea: item.bidArea || "",
          conditionName: item.conditionName || "",
          sectionNumber: item.sectionNumber || "",
        }));

      const combinedLineItems = [...existingLineItems, ...newLineItems];

      if (combinedLineItems.length === 0) {
        setDialog({
          open: true,
          type: "error",
          title: "No Valid Line Items",
          message:
            "At least one line item with a quantity greater than 0 is required.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      await axios.patch(
        `${API_BASE}/progressLogs/${id}`,
        {
          lineItems: combinedLineItems,
          notes: editNotes,
          workCompletedDate: editWorkDate,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await fetchLog();
      setEditMode(false);
      setShowAddItems(false);
      setAdditionalItems([]);
      setAddItemQuantities({});

      setDialog({
        open: true,
        type: "success",
        title: "Log Updated",
        message: `Progress log updated successfully.${newLineItems.length > 0 ? ` ${newLineItems.length} new item(s) added.` : ""}`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Update Failed",
        message:
          err.response?.data?.message || "Failed to update progress log.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Approve / reject / reactivate
  // ---------------------------------------------------------------------------
  const handleUpdateStatus = async (newStatus, reason = "") => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem("token");

      const endpoint =
        newStatus === "approved"
          ? `${API_BASE}/progressLogs/${id}/approve`
          : newStatus === "rejected"
            ? `${API_BASE}/progressLogs/${id}/reject`
            : `${API_BASE}/progressLogs/${id}/reactivate`;

      const payload = newStatus === "rejected" ? { reason } : {};

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const tabFromStatus = {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
      }[newStatus];

      const targetStatusTab = location.state?.activeStatusTab || tabFromStatus;
      const projectId = log?.project?._id || location.state?.projectId;

      if (projectId && location.state?.fromProject) {
        navigate(`/Projects/${projectId}`, {
          state: {
            mainTab: location.state?.mainTab || "Operations",
            initialTab: location.state?.activeTab || "Progress Logs",
            activeStatusTab: targetStatusTab,
          },
        });
      } else {
        navigate("/progress-logs", {
          state: { activeTab: targetStatusTab, fromLogs: true },
        });
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Action Failed",
        message: err.response?.data?.message || "Failed to update status.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const showConfirmationDialog = (status) => {
    if (status === "rejected") {
      setRejectDialogOpen(true);
      return;
    }
    const action = status === "approved" ? "approve" : "reactivate";
    setDialog({
      open: true,
      type: status === "approved" ? "success" : "error",
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      message: `Are you sure you want to ${action} this progress log?`,
      onConfirm: () => {
        setDialog((prev) => ({ ...prev, open: false }));
        handleUpdateStatus(status);
      },
    });
  };

  const handleConfirmReject = () => {
    setRejectDialogOpen(false);
    handleUpdateStatus("rejected", rejectionReason);
    setRejectionReason("");
  };

  // ---------------------------------------------------------------------------
  // Back navigation
  // ---------------------------------------------------------------------------
  const handleBack = () => {
    const targetStatusTab =
      location.state?.activeStatusTab ||
      { pending: "Pending", approved: "Approved", rejected: "Rejected" }[
        log?.status
      ] ||
      "Pending";

    const projectId = log?.project?._id || location.state?.projectId;

    if (projectId && location.state?.fromProject) {
      navigate(`/Projects/${projectId}`, {
        state: {
          mainTab: location.state?.mainTab || "Operations",
          initialTab: location.state?.activeTab || "Progress Logs",
          activeStatusTab: targetStatusTab,
        },
      });
    } else {
      navigate("/progress-logs", { state: { activeTab: targetStatusTab } });
    }
  };

  // ---------------------------------------------------------------------------
  // Derived / computed values
  // ---------------------------------------------------------------------------
  const filteredLineItems = (log?.lineItems || []).filter(
    (item) => !bidAreaFilter || item.bidArea === bidAreaFilter,
  );

  const filteredAdditionalItems = additionalItems.filter(
    (item) => !addItemBidAreaFilter || item.bidArea === addItemBidAreaFilter,
  );

  const addedItemCount = additionalItems.filter(
    (item) => parseFloat(addItemQuantities[item._id] || 0) > 0,
  ).length;

  // Overall WO progress: approved / estimated across all items on this log
  const progressStats = useMemo(() => {
    if (!log?.lineItems?.length)
      return { approved: 0, pending: 0, estimated: 0, pct: 0 };
    const estimated = log.lineItems.reduce(
      (s, i) => s + (i.currentEstimated || 0),
      0,
    );
    const approved = log.lineItems.reduce(
      (s, i) => s + (i.currentApproved || 0),
      0,
    );
    const pending = log.lineItems.reduce(
      (s, i) => s + (i.currentPending || 0),
      0,
    );
    const pct =
      estimated > 0
        ? Math.min(100, ((approved + pending) / estimated) * 100)
        : 0;
    return { approved, pending, estimated, pct };
  }, [log]);

  // Totals row
  const totals = useMemo(() => {
    if (!log?.lineItems)
      return {
        reported: 0,
        estimated: 0,
        approved: 0,
        pending: 0,
        available: 0,
      };
    return {
      reported: filteredLineItems.reduce(
        (s, i) => s + (i.qtyInstalled || 0),
        0,
      ),
      estimated: filteredLineItems.reduce(
        (s, i) => s + (i.currentEstimated || 0),
        0,
      ),
      approved: filteredLineItems.reduce(
        (s, i) => s + (i.currentApproved || 0),
        0,
      ),
      pending: filteredLineItems.reduce(
        (s, i) => s + (i.currentPending || 0),
        0,
      ),
      available: filteredLineItems.reduce(
        (s, i) => s + (i.currentAvailable || 0),
        0,
      ),
    };
  }, [filteredLineItems]);

  // Grouped view
  const groupedLineItems = useMemo(() => {
    if (!groupByTitle) return null;
    const map = new Map();

    filteredLineItems.forEach((item) => {
      const key = `${(item.title || "Untitled").trim()}||${item.uom || ""}||${item.bidArea || ""}`;
      if (!map.has(key)) {
        map.set(key, {
          _groupKey: key,
          title: item.title || "Untitled",
          uom: item.uom || "",
          bidArea: item.bidArea || "",
          _totalEstimated: 0,
          _totalApproved: 0,
          _totalPending: 0,
          _totalAvailable: 0,
          _totalReported: 0,
          _count: 0,
          _hasVariance: false,
          _hasClosed: false,
          _hasNewSpec: false,
          _assemblies: new Set(),
        });
      }
      const g = map.get(key);
      g._count += 1;
      g._totalEstimated += item.currentEstimated || 0;
      g._totalApproved += item.currentApproved || 0;
      g._totalPending += item.currentPending || 0;
      g._totalAvailable += item.currentAvailable || 0;
      g._totalReported += item.qtyInstalled || 0;
      if (item.isFromVariance) g._hasVariance = true;
      if (item.closedOutBySizeChange) g._hasClosed = true;
      if (item.isNewSpec) g._hasNewSpec = true;
      if (item.conditionName) g._assemblies.add(item.conditionName);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.title.localeCompare(b.title, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [filteredLineItems, groupByTitle]);

  // ---------------------------------------------------------------------------
  // Loading / error guards
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-gray-200 text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-red-400 text-lg">{error}</div>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-700">
        <div className="text-red-400 text-lg">Log not found.</div>
      </div>
    );
  }

  const { bar: progressBar, text: progressText } = getProgressColor(
    progressStats.pct,
  );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-700">
      <div className="mx-auto px-4 py-6">
        {/* Back button */}
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            <ArrowLeftIcon className="size-4 mr-2" />
            Back to Progress Logs
          </button>
        </div>

        <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
          <div className="p-6">
            {/* ── Page title + edit button ── */}
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-blue-400">
                Progress Log Details
              </h1>
              {log.status === "pending" && !editMode && (
                <button
                  onClick={enterEditMode}
                  className="px-4 py-2 text-sm font-semibold text-gray-200 bg-blue-500 rounded-md hover:bg-blue-600 transition-colors duration-200"
                >
                  Edit Log
                </button>
              )}
            </div>

            {/* ── Header metadata grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Work Order
                </p>
                <p className="text-sm text-gray-200">
                  {log.workOrder?.title || "N/A"}
                </p>
                <p className="text-xs text-gray-400">{log.woNumber}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Status
                </p>
                <p
                  className={`text-sm font-medium ${
                    log.status === "approved"
                      ? "text-green-400"
                      : log.status === "rejected"
                        ? "text-red-400"
                        : "text-yellow-400"
                  }`}
                >
                  {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Work Completed Date
                </p>
                {editMode ? (
                  <input
                    type="date"
                    value={editWorkDate}
                    onChange={(e) => setEditWorkDate(e.target.value)}
                    className="bg-gray-600 border border-gray-500 rounded-md px-3 py-1.5 text-sm text-gray-200 focus:ring-blue-500 focus:border-blue-500 mt-0.5"
                  />
                ) : (
                  <p className="text-sm text-gray-200">
                    {log.workCompletedDate
                      ? format(
                          parse(
                            log.workCompletedDate.split("T")[0],
                            "yyyy-MM-dd",
                            new Date(),
                          ),
                          "MMMM d, yyyy",
                        )
                      : "N/A"}
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Submitted By
                </p>
                <p className="text-sm text-gray-200">
                  {log.user?.displayName || log.user?.email || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Submitted At
                </p>
                <p className="text-sm text-gray-200">
                  {log.submittedAt
                    ? format(parseISO(log.submittedAt), "MMMM d, yyyy, h:mm a")
                    : "N/A"}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase">
                  Project
                </p>
                <p className="text-sm text-gray-200">
                  {log.project?.projectName || "N/A"}
                </p>
              </div>

              {log.status === "approved" && log.approvedBy && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase">
                    Approved By
                  </p>
                  <p className="text-sm text-green-400">
                    {log.approvedBy?.displayName ||
                      log.approvedBy?.email ||
                      "N/A"}
                  </p>
                </div>
              )}

              {log.status === "rejected" && log.rejectionReason && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase">
                    Rejection Reason
                  </p>
                  <p className="text-sm text-red-400">{log.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* ── Notes ── */}
            <div className="mb-6">
              <p className="text-xs font-medium text-gray-400 uppercase mb-1">
                Notes
              </p>
              {editMode ? (
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  placeholder="Add notes..."
                  className="w-full bg-gray-600 border border-gray-500 rounded-md px-3 py-2 text-sm text-gray-200 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              ) : (
                <p className="text-sm text-gray-200">
                  {log.notes || (
                    <span className="text-gray-500 italic">No notes</span>
                  )}
                </p>
              )}
            </div>

            {/* ── WO Progress bar ── */}
            <div className="mb-6 bg-gray-800/50 rounded-lg p-4 border border-gray-600">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-semibold text-gray-300">
                  Work Order Progress (approved + pending vs estimated)
                </p>
                <span className={`text-sm font-bold ${progressText}`}>
                  {progressStats.pct.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-3">
                <div
                  className={`${progressBar} h-3 rounded-full transition-all duration-500`}
                  style={{ width: `${Math.min(progressStats.pct, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span>
                  Approved:{" "}
                  <span className="text-green-400 font-medium">
                    {progressStats.approved.toLocaleString()}
                  </span>
                </span>
                <span>
                  Pending:{" "}
                  <span className="text-yellow-400 font-medium">
                    {progressStats.pending.toLocaleString()}
                  </span>
                </span>
                <span>
                  Estimated:{" "}
                  <span className="text-gray-200 font-medium">
                    {progressStats.estimated.toLocaleString()}
                  </span>
                </span>
              </div>
            </div>

            {/* ── Line Items header ── */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-blue-400">
                  Line Items
                </h2>
                {!editMode && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-300 whitespace-nowrap">
                      Group by Title
                    </label>
                    <div
                      className={`relative inline-flex h-6 w-11 cursor-pointer items-center rounded-full transition-colors ${
                        groupByTitle ? "bg-blue-500" : "bg-gray-600"
                      }`}
                      onClick={() => setGroupByTitle(!groupByTitle)}
                    >
                      <span
                        className={`absolute left-1 top-1 block h-4 w-4 rounded-full bg-white transition-transform ${
                          groupByTitle ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </div>
                  </div>
                )}
              </div>
              {bidAreas.length > 0 && (
                <select
                  value={bidAreaFilter}
                  onChange={(e) => setBidAreaFilter(e.target.value)}
                  className="block text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Bid Areas</option>
                  {bidAreas.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* ── Line Items table ── */}
            <div className="overflow-x-auto rounded-lg shadow-xl">
              {!editMode && groupByTitle && groupedLineItems ? (
                /* GROUPED VIEW */
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      {[
                        "Title",
                        "Bid Area",
                        "Items",
                        "Net Qty",
                        "Approved",
                        "Pending",
                        "Available",
                        "This Log",
                      ].map((h) => (
                        <th
                          key={h}
                          className={`px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider ${
                            h === "Title" || h === "Bid Area"
                              ? "text-left"
                              : "text-right"
                          } ${h === "Items" ? "text-center" : ""}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {groupedLineItems.length > 0 ? (
                      <>
                        {groupedLineItems.map((group, index) => {
                          const availPct =
                            group._totalEstimated > 0
                              ? (group._totalAvailable /
                                  group._totalEstimated) *
                                100
                              : 0;
                          const availColor =
                            availPct >= 60
                              ? "text-green-400"
                              : availPct >= 20
                                ? "text-yellow-400"
                                : "text-red-400";
                          const logPct =
                            group._totalEstimated > 0
                              ? Math.min(
                                  100,
                                  (group._totalReported /
                                    group._totalEstimated) *
                                    100,
                                )
                              : 0;

                          return (
                            <tr
                              key={group._groupKey}
                              className={`${
                                index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"
                              } hover:bg-gray-500 transition-colors duration-200`}
                            >
                              <td className="px-4 py-2 text-sm text-gray-200">
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {group.title}
                                  </span>
                                  <div className="flex flex-wrap gap-1 mt-0.5">
                                    {group._hasNewSpec && (
                                      <span className="px-1.5 py-0.5 text-xs bg-blue-400/20 text-blue-400 rounded">
                                        NEW SPEC
                                      </span>
                                    )}
                                    {group._hasClosed && (
                                      <span className="px-1.5 py-0.5 text-xs bg-gray-500/50 text-gray-300 rounded">
                                        CLOSED
                                      </span>
                                    )}
                                  </div>
                                  {group._assemblies.size > 0 && (
                                    <span className="text-xs text-gray-500 mt-0.5">
                                      {[...group._assemblies]
                                        .slice(0, 3)
                                        .join(", ")}
                                      {group._assemblies.size > 3 &&
                                        ` +${group._assemblies.size - 3} more`}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {group.bidArea || "N/A"}
                              </td>
                              <td className="px-4 py-2 text-sm text-center">
                                <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-500/50 text-gray-300 rounded-full">
                                  {group._count}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-200 text-right">
                                {group._totalEstimated.toLocaleString()}{" "}
                                <span className="text-gray-400 text-xs">
                                  {group.uom}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-green-400 text-right">
                                {group._totalApproved.toLocaleString()}{" "}
                                <span className="text-gray-400 text-xs">
                                  {group.uom}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-yellow-400 text-right">
                                {group._totalPending.toLocaleString()}{" "}
                                <span className="text-gray-400 text-xs">
                                  {group.uom}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-right">
                                <span className={availColor}>
                                  {group._totalAvailable.toLocaleString()}{" "}
                                  {group.uom}
                                </span>
                                <div className="w-20 bg-gray-600 rounded-full h-1.5 mt-1">
                                  <div
                                    className={`${availColor.replace("text-", "bg-")} h-1.5 rounded-full`}
                                    style={{
                                      width: `${Math.min(Math.max(availPct, 0), 100)}%`,
                                    }}
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm text-right">
                                <span className="text-gray-200 font-medium">
                                  {group._totalReported.toLocaleString()}{" "}
                                  <span className="text-gray-400 text-xs">
                                    {group.uom}
                                  </span>
                                </span>
                                {group._totalEstimated > 0 && (
                                  <div className="flex items-center justify-end gap-1 mt-1">
                                    <div className="w-16 bg-gray-600 rounded-full h-1.5">
                                      <div
                                        className="bg-blue-400 h-1.5 rounded-full"
                                        style={{ width: `${logPct}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-blue-400">
                                      {logPct.toFixed(0)}%
                                    </span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Totals row */}
                        <tr className="bg-gray-800/90 font-bold border-t-2 border-gray-500">
                          <td
                            className="px-4 py-3 text-sm text-blue-300 text-right"
                            colSpan="2"
                          >
                            TOTALS ({groupedLineItems.length} groups,{" "}
                            {groupedLineItems.reduce((s, g) => s + g._count, 0)}{" "}
                            items)
                          </td>
                          <td className="px-4 py-3" />
                          <td className="px-4 py-3 text-sm text-gray-200 text-right">
                            {groupedLineItems
                              .reduce((s, g) => s + g._totalEstimated, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-green-400 text-right">
                            {groupedLineItems
                              .reduce((s, g) => s + g._totalApproved, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-yellow-400 text-right">
                            {groupedLineItems
                              .reduce((s, g) => s + g._totalPending, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200 text-right">
                            {groupedLineItems
                              .reduce((s, g) => s + g._totalAvailable, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-blue-400 font-bold text-right">
                            {groupedLineItems
                              .reduce((s, g) => s + g._totalReported, 0)
                              .toLocaleString()}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan="8"
                          className="px-4 py-4 text-center text-sm text-gray-400"
                        >
                          No line items found
                          {bidAreaFilter && ` for bid area "${bidAreaFilter}"`}.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                /* FLAT VIEW (edit or read-only) */
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      {[
                        { label: "Title", align: "left" },
                        { label: "Assembly", align: "left" },
                        { label: "Bid Area", align: "left" },
                        { label: "Net Qty", align: "right" },
                        { label: "Approved", align: "right" },
                        { label: "Pending", align: "right" },
                        { label: "Available", align: "left" },
                        {
                          label: editMode ? "New Qty" : "Reported",
                          align: "right",
                        },
                        { label: "Progress", align: "right" },
                      ].map((col) => (
                        <th
                          key={col.label}
                          className={`px-4 py-2 text-${col.align} text-xs font-medium text-gray-400 uppercase tracking-wider`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {filteredLineItems.length > 0 ? (
                      <>
                        {filteredLineItems.map((item, index) => {
                          const { color, textColor, percentage, available } =
                            getAvailabilityColor(
                              item.currentEstimated,
                              item.currentApproved,
                              item.currentPending,
                            );
                          const isClosed = item.closedOutBySizeChange === true;

                          // Progress % for this specific line item
                          const itemApproved = item.currentApproved || 0;
                          const itemPending = item.currentPending || 0;
                          const itemEst = item.currentEstimated || 0;
                          const itemPct =
                            itemEst > 0
                              ? Math.min(
                                  100,
                                  ((itemApproved + itemPending) / itemEst) *
                                    100,
                                )
                              : 0;
                          const { bar: itemBar, text: itemText } =
                            getProgressColor(itemPct);

                          return (
                            <tr
                              key={item.lineItemId}
                              className={`${
                                index % 2 === 0 ? "bg-gray-700" : "bg-gray-600"
                              } hover:bg-gray-500 transition-colors duration-200 ${
                                isClosed ? "opacity-60" : ""
                              }`}
                            >
                              {/* Title */}
                              <td className="px-4 py-2 text-sm text-gray-200">
                                <span
                                  className={isClosed ? "line-through" : ""}
                                >
                                  {item.title}
                                </span>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {item.isNewSpec && (
                                    <span className="px-1.5 py-0.5 text-xs bg-blue-400/20 text-blue-400 rounded">
                                      NEW SPEC
                                    </span>
                                  )}
                                  {isClosed && (
                                    <span className="px-1.5 py-0.5 text-xs bg-gray-500/50 text-gray-300 rounded">
                                      CLOSED
                                    </span>
                                  )}
                                  {item.isFromVariance && !item.isNewSpec && (
                                    <span className="px-1.5 py-0.5 text-xs bg-yellow-400/20 text-yellow-400 rounded">
                                      VAR
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Assembly */}
                              <td className="px-4 py-2 text-sm text-gray-200 min-w-48">
                                {item.conditionName || "N/A"}
                                {item.sectionNumber &&
                                  item.sectionNumber !== "-" && (
                                    <span className="text-xs text-gray-400 ml-1">
                                      ({item.sectionNumber})
                                    </span>
                                  )}
                              </td>

                              {/* Bid area */}
                              <td className="px-4 py-2 text-sm text-gray-200">
                                {item.bidArea || "N/A"}
                              </td>

                              {/* Net Qty */}
                              <td className="px-4 py-2 text-sm text-gray-200 text-right">
                                {isClosed ? (
                                  <span className="text-gray-500 line-through">
                                    {(
                                      item.currentEstimated || 0
                                    ).toLocaleString()}
                                  </span>
                                ) : (
                                  <>
                                    {(
                                      item.currentEstimated || 0
                                    ).toLocaleString()}{" "}
                                    <span className="text-gray-400 text-xs">
                                      {item.uom}
                                    </span>
                                  </>
                                )}
                              </td>

                              {/* Approved */}
                              <td className="px-4 py-2 text-sm text-green-400 text-right">
                                {(item.currentApproved || 0).toLocaleString()}{" "}
                                <span className="text-gray-400 text-xs">
                                  {item.uom}
                                </span>
                              </td>

                              {/* Pending */}
                              <td className="px-4 py-2 text-sm text-yellow-400 text-right">
                                {(item.currentPending || 0).toLocaleString()}{" "}
                                <span className="text-gray-400 text-xs">
                                  {item.uom}
                                </span>
                              </td>

                              {/* Available */}
                              <td className="px-4 py-2 text-sm">
                                {isClosed ? (
                                  <span className="text-gray-500">Closed</span>
                                ) : (
                                  <>
                                    <span className={textColor}>
                                      {available.toLocaleString()} {item.uom}
                                    </span>
                                    <div className="w-20 bg-gray-600 rounded-full h-1.5 mt-1">
                                      <div
                                        className={`${color} h-1.5 rounded-full`}
                                        style={{
                                          width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                                        }}
                                      />
                                    </div>
                                  </>
                                )}
                              </td>

                              {/* Reported / editable qty */}
                              <td className="px-4 py-2 text-sm text-gray-200 text-right">
                                {editMode && log.status === "pending" ? (
                                  <input
                                    type="number"
                                    value={quantities[item.lineItemId] ?? ""}
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        item.lineItemId,
                                        e.target.value,
                                      )
                                    }
                                    min="0"
                                    className="w-20 border border-gray-500 rounded-md px-2 py-1 text-sm bg-gray-600 text-gray-200 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                ) : (
                                  <>
                                    {(item.qtyInstalled || 0).toLocaleString()}{" "}
                                    <span className="text-gray-400 text-xs">
                                      {item.uom}
                                    </span>
                                  </>
                                )}
                              </td>

                              {/* Per-item progress bar */}
                              <td className="px-4 py-2 text-sm text-right">
                                {itemEst > 0 && !isClosed ? (
                                  <div className="flex items-center justify-end gap-1">
                                    <div className="w-16 bg-gray-600 rounded-full h-1.5">
                                      <div
                                        className={`${itemBar} h-1.5 rounded-full`}
                                        style={{ width: `${itemPct}%` }}
                                      />
                                    </div>
                                    <span className={`text-xs ${itemText}`}>
                                      {itemPct.toFixed(0)}%
                                    </span>
                                  </div>
                                ) : isClosed ? (
                                  <span className="text-xs text-gray-500">
                                    —
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500">
                                    N/A
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Totals row */}
                        <tr className="bg-gray-800/90 font-bold border-t-2 border-gray-500">
                          <td
                            className="px-4 py-3 text-sm text-blue-300 text-right"
                            colSpan="3"
                          >
                            TOTALS
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200 text-right">
                            {totals.estimated.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-green-400 text-right">
                            {totals.approved.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-yellow-400 text-right">
                            {totals.pending.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200 text-right">
                            {totals.available.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-blue-400 font-bold text-right">
                            {totals.reported.toLocaleString()}
                          </td>
                          <td className="px-4 py-3" />
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan="9"
                          className="px-4 py-4 text-center text-sm text-gray-400"
                        >
                          No line items found
                          {bidAreaFilter && ` for bid area "${bidAreaFilter}"`}.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* ════════════════════════════════════════════════════════════
                 ADD ITEMS FROM WORK ORDER — edit mode only
                 ════════════════════════════════════════════════════════════ */}
            {editMode && (
              <div className="mt-6">
                <button
                  onClick={() => setShowAddItems(!showAddItems)}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 text-gray-200 text-sm font-semibold rounded-md hover:bg-gray-500 transition-colors duration-200 border border-gray-500"
                >
                  <PlusIcon className="size-4 mr-2" />
                  Add Items from Work Order
                  {addedItemCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full font-bold">
                      {addedItemCount}
                    </span>
                  )}
                  {showAddItems ? (
                    <ChevronUpIcon className="size-4 ml-2" />
                  ) : (
                    <ChevronDownIcon className="size-4 ml-2" />
                  )}
                </button>

                {showAddItems && (
                  <div className="mt-4 bg-gray-600/50 rounded-lg border border-gray-500 p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-md font-semibold text-blue-400">
                        Available Work Order Items
                      </h3>
                      {addItemBidAreas.length > 0 && (
                        <select
                          value={addItemBidAreaFilter}
                          onChange={(e) =>
                            setAddItemBidAreaFilter(e.target.value)
                          }
                          className="block text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">All Bid Areas</option>
                          {addItemBidAreas.map((area) => (
                            <option key={area} value={area}>
                              {area}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {addItemsLoading ? (
                      <div className="text-center py-4 text-gray-400">
                        Loading available items...
                      </div>
                    ) : filteredAdditionalItems.length > 0 ? (
                      <div className="overflow-x-auto rounded-lg">
                        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                          <thead className="bg-gray-800">
                            <tr>
                              {[
                                { label: "Title", align: "left" },
                                { label: "Assembly", align: "left" },
                                { label: "Bid Area", align: "left" },
                                { label: "Budget Qty", align: "right" },
                                { label: "Approved", align: "right" },
                                { label: "Pending", align: "right" },
                                { label: "Available", align: "left" },
                                { label: "Qty to Add", align: "right" },
                              ].map((col) => (
                                <th
                                  key={col.label}
                                  className={`px-4 py-2 text-${col.align} text-xs font-medium text-gray-400 uppercase tracking-wider`}
                                >
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-600">
                            {filteredAdditionalItems.map((item, index) => {
                              const { color, textColor, percentage } =
                                getAvailabilityColor(
                                  item.qtyEstimated,
                                  item.qtyInstalled || item.qtyApproved || 0,
                                  item.qtyPending,
                                );
                              const hasQty =
                                parseFloat(addItemQuantities[item._id] || 0) >
                                0;

                              return (
                                <tr
                                  key={item._id}
                                  className={`${
                                    hasQty
                                      ? "bg-blue-400/10 border-l-2 border-l-blue-400"
                                      : index % 2 === 0
                                        ? "bg-gray-700"
                                        : "bg-gray-600"
                                  } hover:bg-gray-500 transition-colors duration-200`}
                                >
                                  <td className="px-4 py-2 text-sm text-gray-200">
                                    {item.title}
                                    {item.hasVariance && (
                                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-400/20 text-yellow-400 rounded">
                                        VAR
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-400 max-w-xs truncate">
                                    {item.conditionName || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-200">
                                    {item.bidArea || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-200 text-right">
                                    {(item.qtyEstimated || 0).toLocaleString()}{" "}
                                    <span className="text-gray-400 text-xs">
                                      {item.uom}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-green-400 text-right">
                                    {(
                                      item.qtyInstalled ||
                                      item.qtyApproved ||
                                      0
                                    ).toLocaleString()}{" "}
                                    <span className="text-gray-400 text-xs">
                                      {item.uom}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-yellow-400 text-right">
                                    {(item.qtyPending || 0).toLocaleString()}{" "}
                                    <span className="text-gray-400 text-xs">
                                      {item.uom}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm">
                                    <span className={textColor}>
                                      {(
                                        item.qtyRemaining || 0
                                      ).toLocaleString()}{" "}
                                      {item.uom}
                                    </span>
                                    <div className="w-20 bg-gray-600 rounded-full h-1.5 mt-1">
                                      <div
                                        className={`${color} h-1.5 rounded-full`}
                                        style={{
                                          width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right">
                                    <input
                                      type="number"
                                      value={addItemQuantities[item._id] || ""}
                                      onChange={(e) =>
                                        handleAddItemQuantityChange(
                                          item._id,
                                          e.target.value,
                                        )
                                      }
                                      placeholder="0"
                                      min="0"
                                      max={item.qtyRemaining}
                                      className="w-20 border border-gray-500 rounded-md px-2 py-1 text-sm bg-gray-600 text-gray-200 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {addedItemCount > 0 && (
                          <div className="mt-3 p-3 bg-gray-700 rounded-md flex justify-between items-center">
                            <span className="text-sm text-gray-300">
                              <span className="font-semibold text-blue-400">
                                {addedItemCount}
                              </span>{" "}
                              item(s) to be added
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 py-4 text-center">
                        {additionalItems.length === 0
                          ? "All available items from this work order are already on the log."
                          : `No items found for bid area "${addItemBidAreaFilter}".`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Summary footer ── */}
            <div className="flex flex-wrap justify-end mt-4 gap-4">
              <p className="text-sm font-medium text-gray-400">
                Total Estimated:{" "}
                <span className="text-lg text-gray-200 font-semibold">
                  {totals.estimated.toLocaleString()}
                </span>
              </p>
              <span className="text-gray-600">|</span>
              <p className="text-sm font-medium text-gray-400">
                Total Approved:{" "}
                <span className="text-lg text-green-400 font-semibold">
                  {totals.approved.toLocaleString()}
                </span>
              </p>
              <span className="text-gray-600">|</span>
              <p className="text-sm font-medium text-gray-400">
                This Log:{" "}
                <span className="text-lg text-blue-400 font-semibold">
                  {(log.totalQtyInstalled || 0).toLocaleString()}
                </span>
              </p>
              <span className="text-gray-600">|</span>
              <p className="text-sm font-medium text-gray-400">
                Overall Progress:{" "}
                <span className={`text-lg font-semibold ${progressText}`}>
                  {progressStats.pct.toFixed(1)}%
                </span>
              </p>
            </div>

            {/* ── Action buttons ── */}
            {(log.status === "pending" || log.status === "rejected") && (
              <div className="flex justify-end space-x-4 mt-6">
                {editMode ? (
                  <>
                    <button
                      onClick={handleSaveChanges}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm font-semibold text-gray-200 bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 transition-colors duration-200"
                    >
                      {actionLoading
                        ? "Saving..."
                        : addedItemCount > 0
                          ? `Save Changes (${addedItemCount} new)`
                          : "Save Changes"}
                    </button>
                    <button
                      onClick={exitEditMode}
                      className="px-4 py-2 text-sm font-semibold text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {log.status === "pending" && (
                      <>
                        <button
                          onClick={() => showConfirmationDialog("approved")}
                          disabled={actionLoading}
                          className="px-4 py-2 text-sm font-semibold text-gray-200 bg-green-500 rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors duration-200"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => showConfirmationDialog("rejected")}
                          disabled={actionLoading}
                          className="px-4 py-2 text-sm font-semibold text-gray-200 bg-red-400 rounded-md hover:bg-red-500 disabled:opacity-50 transition-colors duration-200"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {log.status === "rejected" && (
                      <button
                        onClick={() => showConfirmationDialog("pending")}
                        disabled={actionLoading}
                        className="px-4 py-2 text-sm font-semibold text-gray-200 bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 transition-colors duration-200"
                      >
                        Reactivate
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── General alert dialog ── */}
        <AlertDialog
          open={dialog.open}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          confirmText="Confirm"
          cancelText="Cancel"
          onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
          onConfirm={dialog.onConfirm}
        />

        {/* ── Rejection reason dialog ── */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          className="relative z-10"
        >
          <DialogBackdrop className="fixed inset-0 bg-gray-600/75 transition-opacity" />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl transition-all sm:max-w-lg sm:p-6 border border-gray-600">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setRejectDialogOpen(false)}
                    className="rounded-md text-gray-400 hover:text-gray-200"
                  >
                    <XMarkIcon className="size-5" />
                  </button>
                </div>
                <div className="flex items-start">
                  <div className="mx-auto flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-600 sm:mx-0">
                    <ExclamationTriangleIcon className="size-6 text-red-400" />
                  </div>
                  <div className="mt-3 sm:ml-4 sm:mt-0">
                    <DialogTitle
                      as="h3"
                      className="text-lg font-semibold text-red-400"
                    >
                      Reject Progress Log
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-sm text-gray-300 mb-3">
                        Optionally provide a reason for rejection:
                      </p>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        placeholder="Rejection reason (optional)..."
                        className="w-full bg-gray-600 border border-gray-500 rounded-md px-3 py-2 text-sm text-gray-200 placeholder-gray-400 focus:ring-red-400 focus:border-red-400 resize-none"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-row-reverse gap-3">
                  <button
                    type="button"
                    onClick={handleConfirmReject}
                    className="inline-flex justify-center rounded-md bg-red-400 hover:bg-red-500 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm transition-colors duration-200"
                  >
                    Confirm Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => setRejectDialogOpen(false)}
                    className="inline-flex justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 shadow-sm ring-1 ring-gray-500 hover:bg-gray-500 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default ProgressLogDetail;
